import { useRef } from 'react';

import { IS_PROD, PENDO_API_KEY } from '@/constants';
import { appendScriptIfNotExistsAndWaitForLoad, removeEmptyObjectValues } from '@/utils';


export enum PendoAnalyticsPlatform {
  Web = 'web',
  Mobile = 'mobile',
}

interface InitializeOptions extends Required<Pick<pendo.InitOptions, 'visitor' | 'account'>> {
}

interface UsePendoAnalytics {
  trackEvent: (initOptions: InitializeOptions, eventName: string, properties: pendo.Metadata) => Promise<void>;
}

export const usePendoAnalytics = (): UsePendoAnalytics => {
  const isScriptInitialized = useRef<Promise<boolean> | null>(null);

  const initializeScript = async (initOptions: InitializeOptions): Promise<boolean> => {
    if (isScriptInitialized.current) {
      return isScriptInitialized.current;
    } else if (PENDO_API_KEY) {
      isScriptInitialized.current = new Promise(async (resolve) => {
        try {
          await appendScriptIfNotExistsAndWaitForLoad('pendo_analytics', `https://cdn.pendo.io/agent/static/${ PENDO_API_KEY }/pendo.js`);
          pendo.initialize(initOptions);
          resolve(true);
        } catch (error) {
          console.error('Pendo: initialization failed', error);
          resolve(false);
        }
      });

      return isScriptInitialized.current;
    }

    return false;
  };

  const trackEvent = async (initOptions: InitializeOptions, eventName: string, properties: pendo.Metadata): Promise<void> => {
    // Localhost, or Missing Pendo key
    if (!PENDO_API_KEY) {
      if (IS_PROD) {
        console.error(`Pendo: tracking ${ eventName } failed because PENDO_API_KEY is not set`);
      } else {
        console.log(eventName, properties);
      }
      return;
    }

    // Development, Staging, and Production
    if (await initializeScript(initOptions)) {
      try {
        // Properties with no value is omitted rather than sent empty.
        pendo.track(eventName, removeEmptyObjectValues(properties) as pendo.Metadata);
      } catch (error) {
        console.error(`Pendo: tracking ${ eventName } failed`, error);
      }
    } else {
      console.error(`Pendo: tracking ${ eventName } failed because script initialization failed`);
    }
  };

  return { trackEvent };
};